import * as Sentry from "@sentry/react";
import { posthog } from "utils/posthog";

/**
 * Client-side context attached to an in-product report.
 *
 * Deliberately a flat, closed set of scalars rather than a free-form object.
 * The API validates each field individually (CreateIssueReportDto) and
 * assembles the stored metadata envelope itself, so nothing schema-less
 * crosses the boundary and the server stays the only writer of
 * `source`/`tags`/`priority`.
 *
 * NOT collected, on purpose:
 * - `location.search` / `location.hash` — these carry record ids and filter
 *   values, and a report is retained for as long as the ticket is.
 * - console logs, network logs, DOM scrapes, screenshots — a screenshot of a
 *   reconciliation screen is unmasked customer financial data. Sentry Replay
 *   already captures the session with all text and inputs masked; a user who
 *   *wants* to send an image can still attach one explicitly.
 *
 * `route` IS `location.pathname`, which can still contain record ids
 * (`/company/432/reconciliation/1234/results`). That is accepted: the company
 * is already known to the server, and the path is what makes a report
 * reproducible.
 */
export interface ReportDiagnostics {
	route?: string;
	appVersion?: string;
	sentryEventId?: string;
	sentryReplayId?: string;
	posthogSessionId?: string;
	viewportWidth?: number;
	viewportHeight?: number;
	userAgent?: string;
	clientTime?: string;
}

/**
 * Every lookup is individually guarded. Diagnostics are a nice-to-have on top
 * of the user's own words — a throwing or absent Sentry/PostHog must never
 * stop someone from filing a report.
 */
function safely<T>(fn: () => T): T | undefined {
	try {
		return fn();
	} catch {
		return undefined;
	}
}

/**
 * Capture the route the user is reporting about.
 *
 * Called when the modal OPENS, not when it submits. The modal is mounted once
 * and survives navigation, so a user who wanders off mid-draft would otherwise
 * file a report against whatever page they happened to land on.
 */
export function captureRoute(): string | undefined {
	return safely(() => window.location.pathname);
}

/**
 * Volatile diagnostics, captured at SUBMIT — the Sentry event and replay ids
 * only exist once something has actually gone wrong, which may happen after
 * the modal is already open.
 */
export function collectDiagnostics(route?: string): ReportDiagnostics {
	return {
		route: route ?? captureRoute(),
		appVersion: safely(() => process.env.REACT_APP_VERSION) || undefined,
		sentryEventId: safely(() => Sentry.lastEventId()) || undefined,
		// Usually undefined, and that is expected: Replay is error-sampled, so a
		// replay exists only if Sentry captured an error this session. Anything
		// rendering this must treat absence as a normal state, not a broken link.
		sentryReplayId:
			safely(() => Sentry.getReplay()?.getReplayId()) || undefined,
		posthogSessionId: safely(() => posthog.get_session_id()) || undefined,
		viewportWidth: safely(() => window.innerWidth),
		viewportHeight: safely(() => window.innerHeight),
		userAgent: safely(() => navigator.userAgent?.slice(0, 512)),
		clientTime: safely(() => new Date().toISOString()),
	};
}
